// src/components/CouponModal.jsx
import React, { useEffect, useRef, useState } from "react";

export default function CouponModal({
  coupon,
  onClose = () => {},
  dontShowKey = "coupon_modal_shown_v1",
  persistDontShow = true,
  isLoggedIn = false,
}) {
  const [copied, setCopied] = useState(false);
  const [dontShow, setDontShow] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const timeouts = useRef([]);

  useEffect(() => {
    if (isLoggedIn && persistDontShow) {
      try {
        if (localStorage.getItem(dontShowKey) === "1") setDontShow(true);
      } catch {}
    } else {
      setDontShow(false);
    }
    return () => {
      timeouts.current.forEach((t) => clearTimeout(t));
      timeouts.current = [];
    };
  }, [dontShowKey, persistDontShow, isLoggedIn]);

  const saveDontShow = (v) => {
    if (!persistDontShow) return;
    try {
      if (!isLoggedIn) return;
      if (v) localStorage.setItem(dontShowKey, "1");
      else localStorage.removeItem(dontShowKey);
    } catch {}
  };

  const handleClose = (save = false) => {
    if (save) saveDontShow(true);
    onClose();
  };

  const handleCopy = async () => {
    const text = coupon?.couponCode || "";
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      setCopied(true);
      if (dontShow && isLoggedIn) saveDontShow(true);

      timeouts.current.push(
        setTimeout(() => setCopied(false), 1400),
        setTimeout(() => onClose(), 350)
      );
    } catch (e) {
      onClose();
    }
  };

  if (!coupon) return null;

  const imageUrl =
    coupon.img || coupon.imageUrl || coupon.raw?.imageUrl || coupon.banner || "";

  return (
    <>
      <style>{`
        @keyframes cs-fadeIn {
          0% { opacity: 0; transform: translateY(6px) scale(.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cs-animate { animation: cs-fadeIn 260ms ease-out; }
      `}</style>

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/55 backdrop-blur-sm"
          onClick={() => handleClose(false)}
        />

        <div className="relative w-full max-w-xl cs-animate rounded-2xl overflow-hidden shadow-[0_18px_50px_rgba(0,0,0,0.35)] bg-gradient-to-br from-[#fff8e1] to-[#fff8e1] border border-[#e7d6da] ">

          {/* ✅ Top Right Cross Button */}
          <button
            onClick={() => handleClose(false)}
            className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-white text-[#6b2a35] w-8 h-8 rounded-full shadow-sm flex items-center justify-center transition"
            aria-label="Close"
          >
            ✕
          </button>

          {/* Image */}
          {imageUrl && !imgFailed ? (
            <div className="h-44 md:h-56 w-full bg-white flex items-center justify-center border-b border-[#f0dfe2]">
              <img
                src={imageUrl}
                alt={coupon.alt || coupon.title || "Offer"}
                className="max-h-full max-w-full object-contain"
                onError={() => setImgFailed(true)}
              />
            </div>
          ) : (
            <div className="px-6 py-5 bg-gradient-to-r from-[#91051f] to-[#451111] text-white shadow-inner">
              <h3 className="text-lg md:text-xl font-semibold">{coupon.title || "Special Offer"}</h3>
            </div>
          )}

          <div className="p-5 md:p-6 space-y-4">
            <h4 className="text-lg md:text-xl font-semibold text-[#3d2024]">
              {coupon.title || "Special Offer"}
            </h4>

            <div className="rounded-lg px-4 py-3 bg-white border border-[#e3cfd3] shadow-sm font-mono">
              <div className="text-[11px] text-[#926066]">Code</div>
              <div className="text-lg md:text-xl font-semibold tracking-wider select-all text-[#3b1a20]">
                {coupon.couponCode || "—"}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => handleClose(false)}
                className="rounded-lg px-3 py-2 border border-[#e1c9cc] text-sm text-[#6b2a35] hover:bg-[#fff0f3] transition"
              >
                Close
              </button>

              <button
                onClick={handleCopy}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all ${
                  copied
                    ? "bg-gradient-to-r from-[#1f7f4a] to-[#0f5f3b]"
                    : "bg-gradient-to-r from-[#d10f44] to-[#7c0e29] hover:scale-[1.03]"
                }`}
              >
                {copied ? "Copied ✓" : "Copy code"}
              </button>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-[#5a3a40] select-none">
              <input
                type="checkbox"
                checked={dontShow}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDontShow(v);
                  if (!v && isLoggedIn) saveDontShow(false);
                }}
                className="h-4 w-4 accent-[#8b2530]"
              />
              Don't show again
            </label>
          </div>
        </div>
      </div>
    </>
  );
}
